import { useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/utils/cn'
import { formatDateShort, getWeekday } from '@/lib/date-utils'
import {
  groupGanztagWeekdays,
  type GanztagPlanDay,
  type GanztagPlanEntry,
  type GanztagWeekdaySeries,
} from '@/utils/ganztagWeekdayPlan'

const WEEKDAY_LONG = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'] as const
const WEEKDAY_PLURAL = ['Sonntage', 'Montage', 'Dienstage', 'Mittwoche', 'Donnerstage', 'Freitage', 'Samstage'] as const
const MONTH_LONG = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'] as const

const WD_TONE = [
  { bar: 'bg-slate-400', badge: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600' },
  { bar: 'bg-violet-500', badge: 'bg-violet-50 text-violet-800 border-violet-200 dark:bg-violet-900/30 dark:text-violet-200 dark:border-violet-800' },
  { bar: 'bg-rose-500', badge: 'bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-800' },
  { bar: 'bg-blue-500', badge: 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800' },
  { bar: 'bg-teal-600', badge: 'bg-teal-50 text-teal-800 border-teal-200 dark:bg-teal-900/30 dark:text-teal-200 dark:border-teal-800' },
  { bar: 'bg-amber-500', badge: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800' },
  { bar: 'bg-slate-400', badge: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600' },
] as const

function formatDayHeading(date: string): string {
  const d = new Date(date + 'T00:00:00')
  const wd = WEEKDAY_LONG[d.getDay()] ?? 'Sonntag'
  const month = MONTH_LONG[d.getMonth()] ?? 'Januar'
  return `${wd}, ${d.getDate()}. ${month}`
}

function teacherInitials(name: string): string {
  return name.split(/[, ]/).filter(Boolean).map(s => s[0]).join('').slice(0, 2).toUpperCase()
}

function seriesEntries(series: GanztagWeekdaySeries): GanztagPlanEntry[] {
  for (const day of series.dates) {
    const entries = day.blocks.flatMap(b => b.entries)
    if (entries.length > 0) return [...entries].sort((a, b) => a.rank - b.rank)
  }
  return []
}

function seriesTitle(series: GanztagWeekdaySeries): string {
  if (series.dates.length > 1) return WEEKDAY_PLURAL[series.weekday] ?? 'Serie'
  const date = series.dates[0]?.date
  return date ? formatDayHeading(date) : (WEEKDAY_LONG[series.weekday] ?? 'Tag')
}

function PriorityEntries({ entries }: { entries: GanztagPlanEntry[] }) {
  if (entries.length === 0) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400 px-4 py-2.5">Keine Priorität gesetzt — Rangliste nach Affinität. Der Tag bleibt in der Anfrage.</p>
    )
  }
  return (
    <div>
      {entries.map((entry, idx) => (
        <div
          key={entry.teacherId}
          className={cn(
            'flex justify-between items-center px-4 py-2.5',
            idx < entries.length - 1 && 'border-b border-gray-100 dark:border-gray-800',
          )}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-xs font-bold text-blue-700 dark:text-blue-300 flex-shrink-0">
              {teacherInitials(entry.teacherName)}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[180px]">
                {entry.teacherName}
              </div>
              {entry.teacherCode && (
                <div className="text-xs text-gray-500 dark:text-gray-400">{entry.teacherCode}</div>
              )}
            </div>
          </div>
          <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-full flex-shrink-0">
            #{entry.rank}
          </span>
        </div>
      ))}
    </div>
  )
}

export function GanztagWeekdayPlan({ days }: { days: GanztagPlanDay[] }) {
  const series = useMemo(() => groupGanztagWeekdays(days), [days])
  const hasRepeat = series.some(s => s.dates.length > 1)
  const [openWd, setOpenWd] = useState<number | null>(series[0]?.weekday ?? null)
  const [openDates, setOpenDates] = useState<Record<number, boolean>>({})
  const [hit, setHit] = useState<string | null>(null)

  const resolvedOpen = series.some(s => s.weekday === openWd) ? openWd : (series[0]?.weekday ?? null)

  const openSeries = (wd: number, date?: string) => {
    setOpenWd(wd)
    if (date) {
      setHit(cur => cur === date ? null : date)
      setOpenDates(prev => ({ ...prev, [wd]: true }))
    }
  }

  if (series.length === 0) return null

  return (
    <div data-ganztag-plan>
      <div className="text-[12.5px] leading-relaxed text-blue-900 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl px-3 py-2.5 mb-3">
        {hasRepeat ? (
          <>
            <span className="font-semibold">Wiederholungen sind Serien.</span>
            {' '}Oben jeder Tag als Badge. Darunter Akkordeon pro Wochentag. Einzelne Tage nur innen aufzählen.
          </>
        ) : (
          <>
            <span className="font-semibold">Jeder Tag läuft für sich.</span>
            {' '}Ablehnung oder Timeout gilt nur für diesen Tag.
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {series.flatMap(s => s.dates.map(d => {
          const tone = WD_TONE[s.weekday] ?? WD_TONE[0]!
          return (
            <button
              key={d.date}
              type="button"
              data-date={d.date}
              data-weekday={s.weekday}
              aria-pressed={hit === d.date}
              onClick={() => openSeries(s.weekday, d.date)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs font-semibold',
                tone.badge,
                hit === d.date && 'ring-2 ring-slate-900 dark:ring-white',
              )}
            >
              {getWeekday(d.date)} {formatDateShort(d.date)}
            </button>
          )
        }))}
      </div>

      <div className="space-y-2">
        {series.map(s => {
          const open = s.weekday === resolvedOpen
          const heading = seriesTitle(s)
          const entries = seriesEntries(s)
          const teacherCount = new Set(entries.map(e => e.teacherId)).size
          const personLabel = teacherCount === 1 ? 'Person' : 'Personen'
          const first = s.dates[0]
          const lessonCount = first?.lessonCount ?? 0
          const datesOpen = !!openDates[s.weekday]
          const plural = WEEKDAY_PLURAL[s.weekday] ?? 'Tage'
          const tone = WD_TONE[s.weekday] ?? WD_TONE[0]!
          const sub = s.dates.length > 1
            ? `${s.dates.map(d => formatDateShort(d.date)).join(' · ')} · ${teacherCount} in der Rangliste`
            : `${lessonCount} Lektion${lessonCount !== 1 ? 'en' : ''} · ${teacherCount} ${personLabel} in der Rangliste`
          const singleDate = s.dates.length === 1 ? s.dates[0]?.date : undefined

          return (
            <div
              key={s.weekday}
              className={cn(
                'border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden',
                open && 'border-indigo-200 dark:border-indigo-800',
              )}
            >
              <button
                type="button"
                data-weekday={s.weekday}
                data-date={singleDate}
                aria-expanded={open}
                aria-label={heading}
                onClick={() => setOpenWd(s.weekday)}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2.5 text-left',
                  open ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800',
                )}
              >
                <span className={cn('w-0.5 h-7 rounded-full flex-shrink-0', tone.bar)} />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">{heading}</span>
                  <span className="block text-[11px] text-gray-500 dark:text-gray-400">{sub}</span>
                </span>
                <span className={cn(
                  'text-[11px] font-extrabold tracking-wide rounded-full px-2 py-0.5 flex-shrink-0',
                  s.dates.length > 1
                    ? 'text-indigo-700 bg-indigo-50 dark:text-indigo-300 dark:bg-indigo-900/40'
                    : 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-gray-800 font-semibold tracking-normal',
                )}>
                  {s.dates.length > 1 ? `${s.dates.length}×` : '1 Tag'}
                </span>
                <ChevronDown className={cn(
                  'w-4 h-4 text-gray-400 flex-shrink-0 transition-transform',
                  open && 'rotate-180',
                )} />
              </button>
              {open && (
                <div className="border-t border-gray-200 dark:border-gray-700 px-1 pb-2 pt-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400 px-3 mb-1">
                    {first?.blocks[0]?.blockName || `${lessonCount} Lektion${lessonCount !== 1 ? 'en' : ''}`}
                  </p>
                  {first && first.blocks.length > 1 ? (
                    <div className="space-y-2">
                      {first.blocks.map(block => (
                        <div key={block.id}>
                          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 px-4 py-1">
                            {block.blockName || 'Block'}
                            <span className="ml-2 font-normal text-gray-400">{block.timeSlotIds.length} Lekt.</span>
                          </div>
                          <PriorityEntries entries={[...block.entries].sort((a, b) => a.rank - b.rank)} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <PriorityEntries entries={entries} />
                  )}
                  {s.dates.length > 1 && (
                    <>
                      <div className="mx-3 mt-2 text-[11.5px] leading-snug text-blue-800 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/20 rounded-lg px-2.5 py-2">
                        Ränge gelten für alle {plural}. Jeder einzelne Tag läuft trotzdem für sich.
                      </div>
                      <div className="mx-3 mt-2 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          data-list-dates={s.weekday}
                          aria-expanded={datesOpen}
                          onClick={() => setOpenDates(prev => ({ ...prev, [s.weekday]: !prev[s.weekday] }))}
                          className="w-full flex items-center gap-2 px-2.5 py-2 text-left"
                        >
                          <ChevronDown className={cn(
                            'w-3.5 h-3.5 text-gray-400 transition-transform',
                            datesOpen && 'rotate-180',
                          )} />
                          <span>
                            <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                              {s.dates.length} {plural} aufzählen
                            </span>
                            <span className="text-[11px] text-gray-500"> · nicht als eigene Anfrage-Karten</span>
                          </span>
                        </button>
                        {datesOpen && (
                          <ul className="px-2 pb-2 space-y-0.5">
                            {s.dates.map(d => (
                              <li key={d.date}>
                                <button
                                  type="button"
                                  data-date={d.date}
                                  onClick={() => setHit(cur => cur === d.date ? null : d.date)}
                                  className={cn(
                                    'w-full flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px]',
                                    hit === d.date && 'bg-gray-50 dark:bg-gray-800 ring-1 ring-slate-300 dark:ring-slate-600',
                                  )}
                                >
                                  <span className="font-semibold text-gray-900 dark:text-gray-100">{formatDayHeading(d.date)}</span>
                                  <span className="text-[11px] text-gray-500">
                                    {d.lessonCount} Lekt.
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
